/**
 * Hook for managing photo data
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getPhotosGrouped,
  getPhotoCounts,
  getCountryPhotos,
  replaceCountryPhotos,
  type Photo,
} from '@/lib/photo-api';

export function usePhotos() {
  const [photos, setPhotos] = useState<Record<string, Photo[]>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [photosData, countsData] = await Promise.all([
        getPhotosGrouped(),
        getPhotoCounts(),
      ]);
      setPhotos(photosData);
      setCounts(countsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch photos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const fetchCountryPhotos = useCallback(
    async (countryCode: string): Promise<Photo[]> => {
      return getCountryPhotos(countryCode);
    },
    []
  );

  const saveCountryPhotos = useCallback(
    async (
      countryCode: string,
      photoList: Array<{
        photo_url: string;
        unsplash_id?: string;
        photographer?: string;
        photographer_url?: string;
      }>
    ): Promise<Photo[]> => {
      const savedPhotos = await replaceCountryPhotos(countryCode, photoList);
      // Update local state
      setPhotos((prev) => ({
        ...prev,
        [countryCode]: savedPhotos,
      }));
      setCounts((prev) => ({
        ...prev,
        [countryCode]: savedPhotos.length,
      }));
      return savedPhotos;
    },
    []
  );

  return {
    photos,
    counts,
    loading,
    error,
    refetch: fetchPhotos,
    fetchCountryPhotos,
    saveCountryPhotos,
  };
}
